import React, { useState } from 'react';
import { trackLeft } from './scheduleData';
import TestPeekModal from '../TestingBoard/TestPeekModal';

const LABEL_WIDTH = 200;

const ScheduleTimeline = ({ schedule, onOpenTest, axisLabel = 'LOCATION', compact = false }) => {
    const [selectedTestId, setSelectedTestId] = useState(null);

    const handleBarClick = (testId) => {
        setSelectedTestId(testId);
        onOpenTest?.(testId);
    };

    return (
        <div className="w-full">
            <div className="overflow-x-auto">
                <div className="min-w-[900px]">
                    {/* Axis Header */}
                    <div className={`grid grid-cols-[200px_repeat(6,1fr)] items-center border-b border-border bg-surface-alt text-stride-muted ${compact ? 'h-10 text-[10px]' : 'h-[54px] text-[11px]'}`}>
                        <span className="pl-4 font-bold tracking-wider uppercase">{axisLabel}</span>
                        {schedule.weeks.map((week, index) => (
                            <span key={week} className="pl-4">
                                {week}
                                {index === 1 && <b className={`block text-primary font-bold ${compact ? 'mt-0.5 text-[8px]' : 'mt-1 text-[9px]'}`}>THIS WEEK</b>}
                            </span>
                        ))}
                    </div>

                    {/* Timeline Body */}
                    <div className="relative">
                        {/* Today line */}
                        <div
                            className="absolute z-10 top-0 bottom-0 w-[2px] bg-amber-500 pointer-events-none"
                            style={{ left: trackLeft(schedule.todayFraction, LABEL_WIDTH) }}
                            title="Today"
                        />

                        {schedule.lanes.length === 0 ? (
                            <div className="py-12 text-center text-[13px] text-stride-muted">
                                No live or scheduled tests in the current 6-week window
                            </div>
                        ) : (
                            schedule.lanes.map((lane) => (
                                <div
                                    key={lane.name}
                                    className={`grid grid-cols-[200px_1fr] border-b border-border-subtle last:border-b-0 ${compact ? 'min-h-[50px]' : 'min-h-[92px]'}`}
                                >
                                    {/* Lane Label */}
                                    <div className={`flex items-center justify-between gap-2 bg-surface-subtle min-w-0 overflow-hidden ${compact ? 'px-3' : 'px-3.5'}`}>
                                        <strong
                                            title={lane.name}
                                            className={`break-words [overflow-wrap:anywhere] text-stride-body font-bold leading-tight ${compact ? 'text-[11px] line-clamp-2' : 'text-xs line-clamp-3'}`}
                                        >
                                            {lane.name}
                                        </strong>
                                        <span className="text-stride-muted-light text-xs font-normal shrink-0 ml-1">
                                            {lane.capacity}
                                        </span>
                                    </div>

                                    {/* Track & Bars */}
                                    <div className="relative bg-[linear-gradient(to_right,var(--border-subtle)_1px,transparent_1px)] bg-[size:16.6667%_100%]">
                                        {lane.bars.map((bar) => {
                                            const typeClasses = bar.type === 'live'
                                                ? 'border border-primary bg-primary text-white shadow-card'
                                                : bar.type === 'conflict'
                                                    ? 'border-[1.5px] border-dashed border-red-600 bg-red-50 text-red-800 !z-[3]'
                                                    : 'border border-dashed border-primary/40 bg-surface-subtle text-primary';

                                            const sizeClasses = compact
                                                ? 'top-2.5 h-7 px-2.5 gap-1.5 text-[11px]'
                                                : 'top-[27px] h-[38px] px-3.5 gap-2.5 text-xs';

                                            return (
                                                <button
                                                    key={bar.id}
                                                    type="button"
                                                    onClick={() => handleBarClick(bar.id)}
                                                    className={`absolute z-[2] flex items-center overflow-hidden rounded-md text-left whitespace-nowrap cursor-pointer transition-shadow ${typeClasses} ${sizeClasses}`}
                                                    style={{
                                                        left: `${(bar.start / 6) * 100}%`,
                                                        width: `${(bar.span / 6) * 100}%`,
                                                    }}
                                                >
                                                    <strong className="font-semibold">{bar.key}</strong>
                                                    {bar.note && <span className="opacity-75 text-[11px] font-normal">{bar.note}</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Legend */}
            <p className={`m-0 border-t border-border-subtle bg-surface-subtle text-stride-muted font-normal ${compact ? 'py-2.5 px-4 text-[10px]' : 'py-3.5 px-5 text-[11px]'}`}>
                Solid = active (current date between start & end) · dashed = scheduled (future) · red dashed = conflict · orange line = today · click any bar to open the test
            </p>

            {selectedTestId && (
                <TestPeekModal
                    testId={selectedTestId}
                    onClose={() => setSelectedTestId(null)}
                />
            )}
        </div>
    );
};

export default ScheduleTimeline;
